import { ExtendedMenuItem } from '@gestor-web/@core/menu';
import { ContextoAcesso, PermissaoService } from '@gestor-web/app/shared/services';
import { AnaliseReport, relatoriosList } from '@gestor-web/app/pages/private/analises/analises-reports';

export type AnalisesCategoryKey = Extract<
  AnaliseReport['category'],
  'CONFORMIDADE' | 'ANALISE_REDE' | 'INCORPORACAO_DADOS' | 'INDICADORES_IDSS'
>;

const ANALISES_CATEGORY_CONFIG: Record<
  AnalisesCategoryKey,
  {
    menuLabel: string;
    groupLabel: string;
    basePath: string;
    icon: string;
  }
> = {
  CONFORMIDADE: {
    menuLabel: 'Conformidade',
    groupLabel: 'Conformidade',
    basePath: 'conformidade',
    icon: 'bl bl-conformidade',
  },
  ANALISE_REDE: {
    menuLabel: 'Análise de rede',
    groupLabel: 'Análise de Rede',
    basePath: 'analise-de-rede',
    icon: 'bl bl-analise-de-rede',
  },
  INCORPORACAO_DADOS: {
    menuLabel: 'Incorporação de dados',
    groupLabel: 'Incorporação de Dados',
    basePath: 'incorporacao-de-dados',
    icon: 'bl bl-incorporacao-de-dados',
  },
  INDICADORES_IDSS: {
    menuLabel: 'Indicadores IDSS',
    groupLabel: 'Indicadores IDSS',
    basePath: 'indicadores-idss',
    icon: 'bl bl-indicadores-idss',
  },
};

const ANALISES_REPORT_FEATURE_CODES: Record<string, string> = {
  // Conformidade
  'resumo-guias-rede-propria': 'CONFORMIDADE.RESUMO_GUIAS_REDE_PROPRIA_MESMO_CNPJ',
  'resumo-guias-outras-ops': 'CONFORMIDADE.RESUMO_GUIAS_OUTRAS_OPERADORAS',
  'tabela-tuss-incorreta': 'CONFORMIDADE.TABELA_TUSS_INCORRETA_PROCEDIMENTOS_TUSS',
  'cns-repetido': 'CONFORMIDADE.CNS_REPETIDOS_DIFERENTES_BENEFICIARIOS',
  'cpf-repetido': 'CONFORMIDADE.CPF_REPETIDOS_DIFERENTES_BENEFICIARIOS',
  'criticas-operadora-intermediaria': 'CONFORMIDADE.CRITICAS_OPERADORA_INTERMEDIARIA',
  'rejeitados-receita-federal': 'CONFORMIDADE.CRUZAMENTO_DADOS_BENEFICIARIO_RECEITA_FEDERAL',
  'guias-com-pacote': 'CONFORMIDADE.GUIAS_PACOTES',
  'cnes-generico-incompativel': 'CONFORMIDADE.CNES_GENERICO_INCOMPATIVEL_DATASUS',
  'contas-repetidas-com-diferentes-chaves': 'CONFORMIDADE.CONTAS_REPETIDAS_DIFERENTES_CHAVES',

  // Análise de Rede
  'dispersao-urgencia-emergencia': 'ANALISE_REDE.DISPERSAO_URGENCIA_EMERGENCIA',
  'rps-guias-urgencia-emergencia': 'ANALISE_REDE.RPS_GUIAS_URGENCIA_EMERGENCIA_2030',
  'relatorio-prestadores-acreditados-2-6': 'ANALISE_REDE.PRESTADORES_ACREDITADOS_2060',
  'relatorio-prestadores-acreditados-2-7': 'ANALISE_REDE.PRESTADORES_ACREDITADOS_2070',
  'rps-prestadores-odontologicos': 'ANALISE_REDE.PRESTADORES_ODONTOLOGICOS_2050',
  'relatorio-prestadores-2-10': 'ANALISE_REDE.PRESTADORES_2100',
  'rps-prestadores-registrados': 'ANALISE_REDE.PRESTADORES_REGISTRADOS',

  // Incorporação de Dados
  'guias-rejeitadas': 'INCORPORACAO_DADOS.GUIAS_REJEITADAS',
  'lancamentos-rejeitados-valor-preestabelecido': 'INCORPORACAO_DADOS.LANCAMENTOS_REJEITADOS_VALOR_PREESTABELECIDO',
  'lancamentos-rejeitados-fornecimento-direto': 'INCORPORACAO_DADOS.LANCAMENTOS_REJEITADOS_FORNECIMENTO_DIRETO',
  'lancamentos-rejeitados-outras-despesas': 'INCORPORACAO_DADOS.LANCAMENTOS_REJEITADOS_OUTRAS_DESPESAS_ASSISTENCIAIS',

  // Indicadores IDSS
  'ranking-cbo-consultas': 'INDICADORES_IDSS.RANKING_CBO_CONSULTAS_ELETIVAS',
  'consultas-medicas-eletivas': 'INDICADORES_IDSS.CONSULTAS_MEDICAS_ELETIVAS',
  'ranking-medicos-consultas': 'INDICADORES_IDSS.RANKING_MEDICOS_CONSULTAS_ELETIVAS',
  'procedimentos-elegiveis': 'INDICADORES_IDSS.PROCEDIMENTOS_ELEGIVEIS',
  'resumo-consultas-eletivas': 'INDICADORES_IDSS.RESUMO_CONSULTAS_ELETIVAS',
  'estimulo-parto-procedimento': 'INDICADORES_IDSS.ESTIMULO_PARTO_NORMAL_NUMERADOR_DENOMINADOR',
  'fratura-idosos': 'INDICADORES_IDSS.FRATURA_IDOSOS_NUMERADOR',
  'cuidado-integral-crianca': 'INDICADORES_IDSS.CUIDADO_INTEGRAL_CRIANCA_NUMERADOR',
  'preventivo-cancer-utero': 'INDICADORES_IDSS.PREVENTIVO_CANCER_COLO_UTERO_NUMERADOR',
  'cuidado-diabetico': 'INDICADORES_IDSS.CUIDADO_DIABETICO_NUMERADOR',
  'prevencao-carie-procedimentos': 'INDICADORES_IDSS.PREVENCAO_CARIE_NUMERADOR',
  'cuidados-gengiva-numerador': 'INDICADORES_IDSS.CUIDADOS_GENGIVA_NUMERADOR',
  'cuidados-gengiva-denominador': 'INDICADORES_IDSS.CUIDADOS_GENGIVA_DENOMINADOR',
  'cuidado-integral-idoso': 'INDICADORES_IDSS.CUIDADO_INTEGRAL_IDOSO_NUMERADOR_DENOMINADOR',
  'renal-cronico': 'INDICADORES_IDSS.RENAL_CRONICO',
  'consulta-dentista': 'INDICADORES_IDSS.CONSULTA_DENTISTA',
  'diagnosticos-inespecificos': 'INDICADORES_IDSS.DIAGNOSTICOS_INESPECIFICOS_EVENTOS_INTERNACAO',
};

const SUPPORT_SEARCH_MENU = [
  {
    label: 'Documentação',
    items: [
      {
        label: 'Gestor IDSS',
        icon: 'mdi mdi-book-open-page-variant-outline',
        url: 'https://blendus.zendesk.com/hc/pt-br/categories/17784004969869-Documenta%C3%A7%C3%A3o-Gestor-IDSS',
        target: '_blank',
      },
      {
        label: 'FAQs',
        icon: 'mdi mdi-lightbulb-question-outline',
        url: 'https://blendus.zendesk.com/hc/pt-br/categories/17574029212941-FAQ',
        target: '_blank',
      },
      {
        label: 'Capacitações',
        icon: 'mdi mdi-google-classroom',
        url: 'https://blendus.zendesk.com/hc/pt-br/categories/18221763286029-Capacita%C3%A7%C3%B5es',
        target: '_blank',
      },
      {
        label: 'Notas de liberação',
        icon: 'mdi mdi-file-document-outline',
        url: 'https://blendus.zendesk.com/hc/pt-br/articles/19460584726413-Notas-de-vers%C3%A3o-do-sistema',
        target: '_blank',
      },
    ],
  },
  {
    label: 'Atendimento',
    items: [
      {
        label: 'Enviar solicitação',
        icon: 'mdi mdi-send-outline',
        url: 'https://blendus.zendesk.com/hc/pt-br/signin?return_to=https%3A%2F%2Fblendus.zendesk.com%2Fhc%2Fpt-br%2Frequests%2Fnew',
        target: '_blank',
      },
      {
        label: 'Solicitações',
        icon: 'mdi mdi-list-box-outline',
        url: 'https://blendus.zendesk.com/hc/pt-br/requests',
        target: '_blank',
      },
    ],
  },
] as const;

const PROFILE_SEARCH_MENU = [
  {
    label: 'Meu perfil',
    icon: 'mdi mdi-account-edit-outline',
    routerLink: '/profile',
  },
  {
    label: 'Política de segurança da informação',
    icon: 'mdi mdi-shield-lock-outline',
    url: 'assets/lgpd/politica-seguranca-informacao.pdf',
    target: '_blank',
  },
] as const;

export type SearchMenuItem = {
  groupKey: string;
  groupLabel: string;
  label: string;
  icon?: string;
  route?: any[];
  url?: string;
  target?: string;
  locked?: boolean;
  lockedModules?: string[];
  isAdmin?: boolean;
  parentLabel?: string;
};

export type MenuItemLike = {
  label: string;
  icon?: string;
  invisible?: boolean;
  visible?: boolean;
  locked?: boolean;
  lockedModules?: string[];
  isAdmin?: boolean;
  featureCode?: string;
  route?: (operadora: any) => any[];
};

export type MenuGroupLike = {
  key: string;
  label: string;
  items: MenuItemLike[];
};

export type SearchableExternalMenuItem = {
  label?: string;
  icon?: string;
  url?: string;
  target?: string;
  routerLink?: string | any[];
  visible?: boolean;
};

export type SearchableExternalMenuGroup = {
  label?: string;
  items?: SearchableExternalMenuItem[];
};

export function buildMenuGroupSearchItems(params: { menuGroups: MenuGroupLike[]; operadora: any }): SearchMenuItem[] {
  const { menuGroups, operadora } = params;

  if (!operadora?.cd_registro_ans) {
    return [];
  }

  return menuGroups
    .filter(group => group.key !== 'pesquisar')
    .flatMap(group =>
      group.items
        .filter(item => !item.invisible)
        .filter(item => typeof item.route === 'function')
        .map(item => {
          const routeResult = item.route?.(operadora);

          return {
            groupKey: group.key,
            groupLabel: group.label,
            label: item.label,
            icon: item.icon,
            route: Array.isArray(routeResult) ? routeResult : undefined,
            locked: item.locked,
            lockedModules: item.lockedModules || [],
            isAdmin: item.isAdmin,
          } as SearchMenuItem;
        })
        .filter(item => !!item.route),
    );
}

export function buildTopbarSearchItemsWithAccess(params: {
  menu: ExtendedMenuItem[];
  contexto: ContextoAcesso;
  permissaoService: PermissaoService;
  adminOnly?: boolean;
}): SearchMenuItem[] {
  const { menu, contexto, permissaoService, adminOnly = false } = params;
  const isAdmin = contexto.isAdmin;

  if (adminOnly && !isAdmin) {
    return [];
  }

  return menu.flatMap(group => {
    if (group.visible === false || !group.items?.length) {
      return [];
    }

    return (group.items as ExtendedMenuItem[])
      .filter(item => item.visible !== false)
      .map(item => {
        const route = normalizeRouterLink(item.routerLink);

        if (!route?.length) {
          return null;
        }

        const acesso = permissaoService.resolverAcesso(item.featureCode, contexto);

        if (!acesso.visivel) {
          return null;
        }

        return {
          groupKey: `topbar-${slugify(group.label || 'grupo')}`,
          groupLabel: group.label || 'Topbar',
          parentLabel: 'Topbar',
          label: item.label || '',
          icon: item.icon || 'pi pi-angle-right',
          route,
          locked: acesso.locked,
          lockedModules: acesso.lockedModules,
          isAdmin,
        } as SearchMenuItem;
      })
      .filter((item): item is SearchMenuItem => !!item);
  });
}

export function buildSupportSearchItems(): SearchMenuItem[] {
  return buildExternalMenuSearchItems({
    menu: SUPPORT_SEARCH_MENU as unknown as SearchableExternalMenuGroup[],
    groupPrefix: 'topbar-suporte',
    parentLabel: 'Topbar',
  });
}

export function buildProfileSearchItems(): SearchMenuItem[] {
  return buildFlatMenuSearchItems({
    menu: PROFILE_SEARCH_MENU as unknown as SearchableExternalMenuItem[],
    groupKey: 'topbar-perfil',
    groupLabel: 'Perfil',
    parentLabel: 'Topbar',
  });
}

export function buildExternalMenuSearchItems(params: {
  menu: SearchableExternalMenuGroup[];
  groupPrefix: string;
  parentLabel: string;
}): SearchMenuItem[] {
  const { menu, groupPrefix, parentLabel } = params;

  return menu.flatMap(group => {
    if (!group.items?.length) {
      return [];
    }

    return group.items
      .filter(item => item.visible !== false)
      .map(item => {
        const route = normalizeRouterLink(item.routerLink);

        if (!route?.length && !item.url) {
          return null;
        }

        return {
          groupKey: `${groupPrefix}-${slugify(group.label || parentLabel)}`,
          groupLabel: group.label || parentLabel,
          parentLabel,
          label: item.label || '',
          icon: item.icon || 'pi pi-angle-right',
          route,
          url: item.url,
          target: item.target,
        } as SearchMenuItem;
      })
      .filter((item): item is SearchMenuItem => !!item);
  });
}

export function buildFlatMenuSearchItems(params: {
  menu: SearchableExternalMenuItem[];
  groupKey: string;
  groupLabel: string;
  parentLabel?: string;
}): SearchMenuItem[] {
  const { menu, groupKey, groupLabel, parentLabel } = params;

  return menu
    .filter(item => item.visible !== false)
    .map(item => {
      const route = normalizeRouterLink(item.routerLink);

      if (!route?.length && !item.url) {
        return null;
      }

      return {
        groupKey,
        groupLabel,
        parentLabel,
        label: item.label || '',
        icon: item.icon || 'pi pi-angle-right',
        route,
        url: item.url,
        target: item.target,
      } as SearchMenuItem;
    })
    .filter((item): item is SearchMenuItem => !!item);
}

export function buildAnalisesThirdLevelSearchItems(params: {
  operadoraCdRegistroAns: number | string;
  contexto: ContextoAcesso;
  permissaoService: PermissaoService;
  analisesGroup?: MenuGroupLike;
}): SearchMenuItem[] {
  const { operadoraCdRegistroAns, contexto, permissaoService, analisesGroup } = params;
  const isAdmin = contexto.isAdmin;

  if (!operadoraCdRegistroAns) {
    return [];
  }

  return relatoriosList
    .filter(isSupportedAnaliseCategory)
    .filter(report => isAnalisesParentVisible(report.category, analisesGroup))
    // 'criticas-operadora-intermediaria' foi movida para o menu "Críticas"
    .filter(report => report.path !== 'criticas-operadora-intermediaria')
    .reduce<SearchMenuItem[]>((visiveis, report) => {
      const categoryConfig = ANALISES_CATEGORY_CONFIG[report.category];
      const featureCode = ANALISES_REPORT_FEATURE_CODES[report.path];
      const acesso = permissaoService.resolverAcesso(featureCode, contexto);

      // Relatórios não contratados pela operadora também somem da pesquisa, para não
      // oferecerem um atalho para uma tela que o usuário não deve enxergar.
      if (acesso.visivel) {
        visiveis.push({
          groupKey: 'analises',
          groupLabel: 'Análises',
          parentLabel: categoryConfig.groupLabel,
          label: report.name,
          icon: categoryConfig.icon,
          route: [`/operadora/${operadoraCdRegistroAns}/analises/${categoryConfig.basePath}/${report.path}`],
          locked: acesso.locked,
          lockedModules: acesso.lockedModules,
          isAdmin,
        });
      }

      return visiveis;
    }, []);
}

export function isAnalisesParentVisible(category: AnalisesCategoryKey, analisesGroup?: MenuGroupLike): boolean {
  if (!analisesGroup) {
    return true;
  }

  const menuLabel = ANALISES_CATEGORY_CONFIG[category].menuLabel;
  const parentItem = analisesGroup.items.find(item => item.label === menuLabel);

  return !parentItem?.invisible;
}

export function getAnalisesCategoryConfig(category: AnalisesCategoryKey) {
  return ANALISES_CATEGORY_CONFIG[category];
}

export function getAnalisesFeatureCode(reportPath: string): string | undefined {
  return ANALISES_REPORT_FEATURE_CODES[reportPath];
}

function isSupportedAnaliseCategory(
  report: AnaliseReport,
): report is AnaliseReport & { category: AnalisesCategoryKey } {
  return ['CONFORMIDADE', 'ANALISE_REDE', 'INCORPORACAO_DADOS', 'INDICADORES_IDSS'].includes(report.category);
}

function normalizeRouterLink(routerLink?: string | any[]): any[] | undefined {
  if (!routerLink) {
    return undefined;
  }

  if (Array.isArray(routerLink)) {
    return routerLink;
  }

  return [routerLink];
}

function slugify(value: string): string {
  return normalizeText(value).replace(/\s+/g, '-');
}

function normalizeText(value: string): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}
